import {
  type CareMessageDraft,
  type CareThread,
  HELP_LINES,
  OFFLINE_ACTIONS,
  shortDate,
  timeOf,
  todayIso,
  useCareActions,
  useCareMessages,
  useQueuedActions,
  useReportedMessage,
} from '@shkola/api';
import { LifebuoyIcon } from '@phosphor-icons/react/Lifebuoy';
import { LockSimpleIcon } from '@phosphor-icons/react/LockSimple';
import { PauseIcon } from '@phosphor-icons/react/Pause';
import { PhoneIcon } from '@phosphor-icons/react/Phone';
import { PlayIcon } from '@phosphor-icons/react/Play';
import { ShieldWarningIcon } from '@phosphor-icons/react/ShieldWarning';
import type { TFunction } from 'i18next';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Spinner } from '../components/ui';
import {
  DARK,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PillIcon,
  grad,
  pillClass,
  ringSmall,
} from '../design/forms';
import {
  ArrowCounterClockwiseIcon,
  CaretRightIcon,
  ChatCircleIcon,
  CheckIcon,
  type FilledIcon,
  PaperPlaneTiltIcon,
} from '../design/icons';

/*
 * The parts of «Турбота» in «Форми 2» (28.10): calm and warm — the words on white, a soft gradient where
 * something matters, the line pictures; a lock before every promise of privacy (never an emoji). The help
 * lines, the breathing and «5-4-3-2-1», one conversation with the psychologist: the same for the student,
 * the family and the staff.
 */

/** A field to write in: white with a hairline, the dark ring when typing (as the answer of «Домашні»). */
export const FIELD =
  'w-full rounded-[16px] bg-white px-3.5 py-3 text-[15px] leading-6 outline-none ring-1 ring-[#E4E0D7] ring-inset placeholder:text-[rgb(31_26_53/0.42)] focus:ring-2 focus:ring-[var(--sh-dark)]';

/** A promise of privacy: a small lock before the words. */
export function Private({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={`flex min-w-0 items-start gap-2 text-[14px] leading-5 ${className}`}
      style={{ color: INK_SOFT }}
    >
      <LockSimpleIcon size={15} weight="bold" aria-hidden className="mt-[2px] shrink-0" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

/** One of a few to pick in a form: a chip on the paper, the chosen one a shade darker with a tick. */
export function Choice({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon?: FilledIcon;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full px-4 text-[14px] transition-colors ${active ? 'font-medium' : 'hover:bg-[#EFECE5]'}`}
      style={{ backgroundColor: active ? '#E6E1D6' : PAPER, color: active ? INK : INK_SOFT }}
    >
      {active ? (
        <CheckIcon size={13} weight="bold" aria-hidden className="-ml-1" />
      ) : (
        Icon && <Icon size={14} weight="bold" aria-hidden className="-ml-1" />
      )}
      {children}
    </button>
  );
}

/** A small icon on a soft gradient circle (for what has no picture of its own). */
export function IconDot({
  icon: Icon,
  tone,
  size = 40,
}: {
  icon: FilledIcon;
  tone: Grad;
  size?: number;
}) {
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-full"
      style={{ width: size, height: size, backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <Icon size={Math.round(size * 0.44)} weight="fill" />
    </span>
  );
}

/** What a conversation is about, as a picture: help now coral, bullying apricot, a talk lavender. */
export function threadLook(thread: CareThread): { icon: FilledIcon; tone: Grad } {
  if (thread.urgent) return { icon: LifebuoyIcon, tone: 'coral' };
  if (thread.kind === 'bullying' || thread.reportedMessageId)
    return { icon: ShieldWarningIcon, tone: 'apricot' };
  return { icon: ChatCircleIcon, tone: 'lavender' };
}

/** «5–9 клас», «7-Б», «Олійник Кирило, 7-Б» — what the staff know about the author. */
export function useWhoLabel() {
  const { t } = useTranslation();
  return (thread: CareThread) =>
    thread.authorName
      ? `${thread.authorName}${thread.classLabel ? `, ${thread.classLabel}` : ''}`
      : (thread.classLabel ?? t(`care.bands.${thread.gradeBand}`));
}

export function threadTitle(t: TFunction, thread: CareThread): string {
  if (thread.reportedMessageId) return t('care.threads.complaint');
  if (thread.kind === 'bullying') return t('care.threads.bullying');
  return t(`care.topics.${thread.topic}`);
}

const when = (iso: string) => {
  const day = iso.slice(0, 10);
  return day === todayIso() ? timeOf(iso) : `${shortDate(day)} ${timeOf(iso)}`;
};

/** Help lines: a tap calls on a phone; on a computer the number is plain to read. */
export function HelpLines() {
  const { t } = useTranslation();
  return (
    <ul className="grid gap-2 sm:grid-cols-2">
      {HELP_LINES.map((line) => (
        <li key={line.key}>
          <a
            href={`tel:${line.phone}`}
            className="group flex h-full flex-col gap-1.5 rounded-[20px] p-4 transition-colors hover:bg-[#EFECE5]"
            style={{ backgroundColor: PAPER }}
          >
            <span
              className="block font-display text-[30px] leading-[34px] font-light tracking-[-0.03em] whitespace-nowrap"
              style={{ color: INK }}
            >
              {line.phone.length > 6
                ? line.phone.replace(/^(\d{4})(\d{3})(\d{3})$/, '$1 $2 $3')
                : line.phone}
            </span>
            <span className="flex min-w-0 flex-1 items-end gap-3">
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] leading-5 font-medium" style={{ color: INK }}>
                  {t(`care.sos.lines.${line.key}.name`)}
                </span>
                <span className="block text-[12.5px] leading-[17px]" style={{ color: INK_SOFT }}>
                  {t(`care.sos.lines.${line.key}.hint`)}
                </span>
              </span>
              <span className={ringSmall} title={t('care.sos.call')}>
                <PhoneIcon size={14} weight="bold" aria-hidden />
              </span>
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}

const PHASES = ['inhale', 'hold', 'exhale', 'rest'] as const;
const PHASE_SECONDS = 4;

/** Box breathing: the circle grows for four counts, holds, shrinks, rests. Works offline. */
export function Breathing() {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [running]);
  const phase = PHASES[Math.floor(tick / PHASE_SECONDS) % PHASES.length] ?? 'inhale';
  const count = (tick % PHASE_SECONDS) + 1;
  const rounds = Math.floor(tick / (PHASE_SECONDS * PHASES.length));
  const big = running && (phase === 'inhale' || phase === 'hold');
  return (
    <div className="flex flex-col items-center gap-5 py-2">
      <div className="relative flex size-56 items-center justify-center">
        <span
          aria-hidden
          className="absolute inset-0 rounded-full transition-transform ease-in-out"
          style={{
            backgroundImage: grad('sky'),
            transform: `scale(${big ? 1 : 0.55})`,
            transitionDuration: `${PHASE_SECONDS}s`,
          }}
        />
        <span
          aria-hidden
          className="absolute inset-[22%] rounded-full transition-transform ease-in-out"
          style={{
            backgroundImage: grad('lavender'),
            transform: `scale(${big ? 1.12 : 0.7})`,
            transitionDuration: `${PHASE_SECONDS}s`,
          }}
        />
        <span className="relative text-center" aria-live="polite">
          <span
            className={`block font-display font-light tracking-[-0.02em] ${running ? 'text-[26px] leading-8' : 'mx-auto max-w-[120px] text-[19px] leading-6'}`}
            style={{ color: INK }}
          >
            {running ? t(`care.calm.phases.${phase}`) : t('care.calm.breathing')}
          </span>
          {running && (
            <span
              className="block font-display text-[44px] leading-[48px] font-light"
              style={{ color: INK_SOFT }}
            >
              {count}
            </span>
          )}
        </span>
      </div>
      <p className="max-w-xs text-center text-[14px] leading-5" style={{ color: INK_SOFT }}>
        {t('care.calm.breathingHint')}
      </p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setTick(0);
            setRunning((r) => !r);
          }}
          className={pillClass('dark')}
        >
          {running ? t('care.calm.stop') : t('care.calm.start')}
          <PillIcon icon={running ? PauseIcon : PlayIcon} />
        </button>
        {running && rounds > 0 && (
          <span className="text-[14px]" style={{ color: INK_SOFT }}>
            {t('care.calm.rounds', { count: rounds })}
          </span>
        )}
      </div>
    </div>
  );
}

const STEPS = ['see', 'touch', 'hear', 'smell', 'taste'] as const;

/** «5-4-3-2-1»: back to here and now, one sense at a time. */
export function Grounding({ onDone }: { onDone?: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const done = step >= STEPS.length;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-[14px] leading-5" style={{ color: INK_SOFT }}>
        {t('care.calm.groundingHint')}
      </p>
      <ol className="flex flex-col gap-1.5">
        {STEPS.map((key, i) => (
          <li
            key={key}
            className="flex min-h-[52px] items-center gap-3 rounded-[18px] px-4 py-2.5 transition-colors duration-500"
            style={
              i === step
                ? { backgroundImage: grad('butter'), color: INK }
                : { backgroundColor: PAPER, color: i < step ? INK_FAINT : INK_SOFT }
            }
          >
            <span className="w-5 font-display text-[26px] leading-none font-light">{5 - i}</span>
            <span className={`text-[15px] ${i === step ? 'font-medium' : ''}`}>
              {t(`care.calm.steps.${key}`)}
            </span>
            {i < step && (
              <CheckIcon size={15} weight="bold" aria-hidden className="ml-auto shrink-0" />
            )}
          </li>
        ))}
      </ol>
      {done ? (
        <div className="flex flex-wrap items-center gap-2">
          <p
            className="mr-2 font-display text-[22px] leading-7 font-light tracking-[-0.02em]"
            style={{ color: INK }}
          >
            {t('care.calm.done')}
          </p>
          <button type="button" onClick={() => setStep(0)} className={pillClass('light')}>
            {t('care.calm.again')}
            <PillIcon icon={ArrowCounterClockwiseIcon} />
          </button>
          {onDone && (
            <button type="button" onClick={onDone} className={pillClass('dark')}>
              {t('care.mood.question')}
              <PillIcon icon={CaretRightIcon} />
            </button>
          )}
        </div>
      ) : (
        <div>
          <button type="button" onClick={() => setStep((s) => s + 1)} className={pillClass('dark')}>
            {t('care.calm.next')}
            <PillIcon icon={CaretRightIcon} />
          </button>
        </div>
      )}
    </div>
  );
}

/** The quoted chat message a complaint is about, for those who answer it. */
function ReportedMessage({ threadId }: { threadId: string }) {
  const { t } = useTranslation();
  const message = useReportedMessage(threadId, true);
  if (!message.data) return null;
  return (
    <div
      className="rounded-[18px] px-4 py-3"
      style={{ backgroundImage: grad('coral'), color: GRAD.coral.ink }}
    >
      <p className="text-[12.5px] font-medium">{t('care.staff.reported')}</p>
      <p className="mt-1 text-[15px] leading-[22px]" style={{ color: INK }}>
        «{message.data.body}»
      </p>
      <p className="mt-1 text-[12.5px]">
        {message.data.author_name} · {message.data.conversation_title} ·{' '}
        {when(message.data.created_at)}
      </p>
    </div>
  );
}

const SMALL_LIGHT =
  'inline-flex h-8 shrink-0 items-center rounded-full bg-white px-3.5 text-[13px] font-medium whitespace-nowrap ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#FAF9F6]';

/**
 * One conversation, the same for both sides: the student sees «Ти» and the psychologist by name;
 * the psychologist sees what the student chose to show. New messages arrive every few seconds.
 */
export function ThreadView({
  thread,
  side,
  onReveal,
}: {
  thread: CareThread;
  side: 'student' | 'staff';
  onReveal?: (what: { showClass: boolean; showName: boolean }) => void;
}) {
  const { t } = useTranslation();
  const messages = useCareMessages(thread.id);
  const actions = useCareActions();
  const queued = useQueuedActions<CareMessageDraft>(OFFLINE_ACTIONS.careSend).filter(
    (q) => q.threadId === thread.id,
  );
  const [draft, setDraft] = useState('');
  const list = useRef<HTMLDivElement>(null);
  const whoLabel = useWhoLabel();
  const count = messages.data?.length ?? 0;
  const unread = side === 'student' ? thread.unreadForStudent : thread.unreadForStaff;

  // seen on open and whenever something new arrives while it is open
  useEffect(() => {
    if (unread) actions.markRead.mutate(thread.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to new messages only
  }, [thread.id, unread, count]);
  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight });
  }, [count, queued.length]);

  const send = () => {
    const body = draft.trim();
    if (!body) return;
    actions.send.mutate({ threadId: thread.id, body, clientId: crypto.randomUUID() });
    setDraft('');
  };

  return (
    <div className="flex min-h-[440px] min-w-0 flex-1 flex-col">
      <div className="flex flex-col gap-3 pb-3 empty:hidden">
        {side === 'staff' && thread.reportedMessageId && <ReportedMessage threadId={thread.id} />}
        {side === 'staff' && thread.kind === 'bullying' && (
          <p className="flex flex-wrap gap-1.5 empty:hidden">
            {thread.incidentPlace && <Badge>{thread.incidentPlace}</Badge>}
            {thread.aboutSelf !== null && (
              <Badge tone="danger">
                {thread.aboutSelf ? t('care.staff.aboutSelf') : t('care.staff.aboutOther')}
              </Badge>
            )}
          </p>
        )}
        {side === 'student' && (
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[18px] px-4 py-2.5"
            style={{ backgroundColor: PAPER }}
          >
            <Private className="mr-auto">
              {t(thread.kind === 'bullying' ? 'care.thread.seesDirector' : 'care.thread.sees', {
                who: whoLabel(thread),
              })}
            </Private>
            {onReveal && (!thread.classLabel || !thread.authorName) && (
              <span className="flex flex-wrap gap-1.5">
                {!thread.classLabel && (
                  <button
                    type="button"
                    onClick={() => onReveal({ showClass: true, showName: false })}
                    className={SMALL_LIGHT}
                    style={{ color: INK }}
                  >
                    {t('care.thread.revealClass')}
                  </button>
                )}
                {!thread.authorName && (
                  <button
                    type="button"
                    onClick={() => onReveal({ showClass: true, showName: true })}
                    className={SMALL_LIGHT}
                    style={{ color: INK }}
                  >
                    {t('care.thread.revealName')}
                  </button>
                )}
              </span>
            )}
          </div>
        )}
      </div>

      <div
        ref={list}
        className="flex max-h-[62vh] flex-1 flex-col gap-2 overflow-y-auto pr-1"
        aria-live="polite"
      >
        {messages.isPending ? (
          <Spinner />
        ) : (
          (messages.data ?? []).map((m) => {
            const mine = side === 'student' ? m.fromStudent : !m.fromStudent;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[82%] rounded-[20px] px-4 py-2.5 ${mine ? 'rounded-br-[6px]' : 'rounded-bl-[6px]'}`}
                  style={
                    mine
                      ? { backgroundColor: DARK, color: '#FFFFFF' }
                      : { backgroundColor: PAPER, color: INK }
                  }
                >
                  <p
                    className="text-[12px] font-medium"
                    style={{ color: mine ? 'rgb(255 255 255 / 0.62)' : INK_SOFT }}
                  >
                    {m.fromStudent
                      ? side === 'student'
                        ? t('care.thread.you')
                        : whoLabel(thread)
                      : `${m.authorName ?? ''}${m.authorRole ? ` · ${t(`care.thread.roles.${m.authorRole}`)}` : ''}`}
                  </p>
                  <p className="text-[15px] leading-[22px] whitespace-pre-wrap">{m.body}</p>
                  <p
                    className="mt-0.5 text-right text-[11px]"
                    style={{ color: mine ? 'rgb(255 255 255 / 0.5)' : INK_FAINT }}
                  >
                    {when(m.createdAt)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        {queued.map((q) => (
          <div key={q.clientId} className="flex justify-end">
            <div
              className="max-w-[82%] rounded-[20px] rounded-br-[6px] px-4 py-2.5 text-white opacity-70"
              style={{ backgroundColor: DARK }}
            >
              <p className="text-[15px] leading-[22px] whitespace-pre-wrap">{q.body}</p>
              <p className="mt-0.5 text-right text-[11px] text-white/70">
                {t('care.thread.queued')}
              </p>
            </div>
          </div>
        ))}
      </div>

      {thread.status === 'closed' && side === 'student' && (
        <p className="mt-3 text-center text-[14px]" style={{ color: INK_SOFT }}>
          {t('care.thread.closed')}
        </p>
      )}
      <form
        className="mt-3 flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          maxLength={4000}
          aria-label={side === 'student' ? t('care.thread.placeholder') : t('care.staff.reply')}
          placeholder={side === 'student' ? t('care.thread.placeholder') : t('care.staff.reply')}
          className={`${FIELD} min-h-[52px] flex-1 resize-none`}
          style={{ color: INK }}
        />
        <button
          type="submit"
          disabled={!draft.trim()}
          className={`${pillClass('dark')} disabled:opacity-35 max-sm:pl-1.5`}
        >
          <span className="hidden sm:inline">
            {side === 'student' ? t('care.thread.send') : t('care.staff.send')}
          </span>
          <span className="sr-only sm:hidden">
            {side === 'student' ? t('care.thread.send') : t('care.staff.send')}
          </span>
          <PillIcon icon={PaperPlaneTiltIcon} />
        </button>
      </form>
    </div>
  );
}
